/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, ActionButton, Switch, Text } from '@components/atoms'
import {
	GridToolbarContainer,
	GridSlotProps,
	DEFAULT_GRID_AUTOSIZE_OPTIONS,
} from '@mui/x-data-grid'
import XLSX from 'xlsx-js-style'
import { Button as MuiButton, Tooltip } from '@mui/material'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import SettingsEthernetRoundedIcon from '@mui/icons-material/SettingsEthernetRounded'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'

import { useAppSelector } from '@redux/index'
import { grey } from '@mui/material/colors'

// 툴바 모든 아이템(수정 모드 토글, 검색 인풋, 우측 액션 버튼) 공통 높이.
const TOOLBAR_ITEM_HEIGHT = 32

// "차시 추가" 와 동일한 blue gradient 액션 버튼 스타일 (인쇄 / 간격맞춤).
const blueGradientBtnSx = {
	textTransform: 'none',
	whiteSpace: 'nowrap',
	borderRadius: '10px',
	paddingX: 1.25,
	paddingY: 0,
	minWidth: 0,
	height: TOOLBAR_ITEM_HEIGHT,
	color: '#ffffff',
	fontWeight: 700,
	fontSize: '0.8125rem',
	background: 'linear-gradient(135deg, #1d4ed8 0%, #004195 100%)',
	boxShadow: '0 4px 12px rgba(0, 65, 149, 0.25)',
	'&:hover': {
		background: 'linear-gradient(135deg, #1e40af 0%, #003070 100%)',
		boxShadow: '0 6px 16px rgba(0, 65, 149, 0.35)',
	},
	'&.Mui-disabled': {
		background: '#cbd5e1',
		color: '#fff',
	},
} as const

// 엑셀 다운로드 / 업로드 — 출석 관리 페이지의 "엑셀 다운로드" 와 동일한 초록 톤.
const greenGradientBtnSx = {
	...blueGradientBtnSx,
	background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
	boxShadow: '0 4px 12px rgba(21, 128, 61, 0.25)',
	'&:hover': {
		background: 'linear-gradient(135deg, #15803d 0%, #166534 100%)',
		boxShadow: '0 6px 16px rgba(21, 128, 61, 0.35)',
	},
	'&.Mui-disabled': {
		background: '#cbd5e1',
		color: '#fff',
	},
} as const

type PropsType = GridSlotProps['toolbar'] & {
	onAddClick?: () => void
}

export const GridToolBar = (props: PropsType) => {
	const { isLoading, apiRef, title, editModeOn, onEditModeToggle, hasActionColumn } =
		props

	const userLanguage: string = useAppSelector(state => state.user.language)

	const onActionToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
		onEditModeToggle(e.target.checked)
	}

	// "수정 모드" 우측 검색 인풋 — 엔터 시 DataGrid quickFilter 로 모든 가시 컬럼 contains 검색.
	const [searchFocused, setSearchFocused] = React.useState<boolean>(false)
	const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key !== 'Enter') return
		const v = String(e.currentTarget.value ?? '').trim()
		const words = v ? v.split(/\s+/).filter(Boolean) : []
		try {
			apiRef.current?.setQuickFilterValues(words)
		} catch {
			/* noop */
		}
	}

	const addTitle = userLanguage === 'ko' ? '추가' : 'ADD'
	const exportTitle = userLanguage === 'ko' ? '엑셀 다운로드' : 'EXCEL DOWNLOAD'
	const uploadTitle = userLanguage === 'ko' ? '엑셀 업로드' : 'EXCEL UPLOAD'
	const printTitle = userLanguage === 'ko' ? '인쇄' : 'PRINT'
	const autoSizeColumnsTitle = userLanguage === 'ko' ? '간격맞춤' : 'Autosize Columns'

	const onUploadClick = () => {
		if (typeof props.onExcelUploadClick === 'function') {
			props.onExcelUploadClick()
		}
	}
	const includeHeaders = DEFAULT_GRID_AUTOSIZE_OPTIONS.includeHeaders
	const includeOutliers = DEFAULT_GRID_AUTOSIZE_OPTIONS.includeOutliers
	const outliersFactor = String(DEFAULT_GRID_AUTOSIZE_OPTIONS.outliersFactor)
	const expand = DEFAULT_GRID_AUTOSIZE_OPTIONS.expand

	const onAddClick = () => {
		if (typeof props.onAddClick === 'function') {
			props.onAddClick()
		}
	}

	const autosizeOptions = {
		includeHeaders,
		includeOutliers,
		outliersFactor: Number.isNaN(parseFloat(outliersFactor))
			? 1
			: parseFloat(outliersFactor),
		expand,
	}

	const onExportClick = () => {
		const api: any = apiRef.current
		if (!api) return

		const allCols: any[] = api.getAllColumns?.() ?? []
		const visibleCols = allCols.filter(
			c =>
				c.field !== 'idx' &&
				c.field !== 'actions' &&
				c.field !== '__check__' &&
				!c.disableExport &&
				api.getColumn?.(c.field),
		)
		const visibilityModel = api.state?.columns?.columnVisibilityModel ?? {}
		const finalCols = visibleCols.filter(c => visibilityModel[c.field] !== false)

		const rowIds: any[] =
			api.getSortedRowIds?.() ?? Array.from((api.getRowModels?.() ?? new Map()).keys())

		const headers = finalCols.map((c: any) => c.headerName ?? c.field)
		const colWidths = headers.map((h: string) => h.length)

		const sheetData: any[][] = [headers]
		const cellStyles: Record<string, any> = {}

		const hdrFill = { fgColor: { rgb: '1E3A5F' } }
		const hdrFont = { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 }
		headers.forEach((_: string, ci: number) => {
			const addr = XLSX.utils.encode_cell({ r: 0, c: ci })
			cellStyles[addr] = {
				fill: hdrFill,
				font: hdrFont,
				alignment: { horizontal: 'center', vertical: 'center' },
			}
		})

		rowIds.forEach((id: any, ri: number) => {
			const row = api.getRow(id)
			const rowData: any[] = []
			finalCols.forEach((col: any, ci: number) => {
				let value: any = row?.[col.field]
				let style: any = null
				if (typeof col.exportCell === 'function') {
					const r = col.exportCell(value, row)
					value = r?.v ?? value
					style = r?.s ?? null
				} else {
					if (typeof col.valueGetter === 'function') {
						value = col.valueGetter(value, row, col, { current: api })
					}
					if (typeof col.valueFormatter === 'function') {
						value = col.valueFormatter(value, row, col, { current: api })
					}
				}
				if (value instanceof Date) value = value.toISOString()
				const str = value == null ? '' : String(value)
				if (str.length > colWidths[ci]) colWidths[ci] = str.length
				rowData.push(value)
				if (style) {
					const addr = XLSX.utils.encode_cell({ r: ri + 1, c: ci })
					cellStyles[addr] = style
				}
			})
			sheetData.push(rowData)
		})

		const ws = XLSX.utils.aoa_to_sheet(sheetData)
		ws['!cols'] = colWidths.map((w: number) => ({ wch: Math.max(w + 3, 8) }))
		for (const [addr, s] of Object.entries(cellStyles)) {
			if (ws[addr]) ws[addr].s = s
		}

		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
		const dateStr = new Date().toISOString().slice(0, 10)
		const fileName = title ? `${title} ${dateStr}.xlsx` : `export ${dateStr}.xlsx`
		XLSX.writeFile(wb, fileName)
	}

	const onPrintClick = () => {
		apiRef.current.exportDataAsPrint({
			hideToolbar: true,
			hideFooter: true,
		})
	}

	return (
		<GridToolbarContainer>
			<View css={style}>
				{!isLoading && (
					<>
						<View className="leftToolView">
							{hasActionColumn && (
								<View className={`actionToggle${editModeOn ? ' actionToggleOn' : ''}`}>
									<Text category="s1" className="actionToggleLabel">
										수정 모드
									</Text>
									<Switch size="small" checked={editModeOn} onChange={onActionToggle} />
								</View>
							)}
							{/* "수정 모드" 우측 — 엔터 시 모든 가시 컬럼 quickFilter 검색.
								초기 작은 사이즈 → 포커스 시 ~3배 너비로 부드럽게 확장.
								반응형 max-width 로 우측 버튼 그룹과 부딪히지 않게 제한. */}
							<View className={`gridSearchInput${searchFocused ? ' focused' : ''}`}>
								<SearchRoundedIcon className="searchIcon" />
								<input
									type="text"
									aria-label="테이블 검색"
									onFocus={() => setSearchFocused(true)}
									onBlur={() => setSearchFocused(false)}
									onKeyDown={onSearchKeyDown}
								/>
							</View>
							{editModeOn && (
								<ActionButton
									iconName="plus"
									title={addTitle}
									variant="contained"
									buttonColor="primary"
									iconTextColor="white"
									onClick={onAddClick}
								/>
							)}
							{props.leftToolbarSlot ? (
								<View className="leftToolbarSlot">{props.leftToolbarSlot}</View>
							) : null}
						</View>
						<View className="leftToolView rightToolGroup">
							<Tooltip title={exportTitle}>
								<span>
									<MuiButton
										variant="contained"
										sx={greenGradientBtnSx}
										className="gridRightBtn"
										onClick={onExportClick}
									>
										<View className="gridRightBtnInner">
											<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
											<span>{exportTitle}</span>
										</View>
									</MuiButton>
								</span>
							</Tooltip>
							{/* 엑셀 업로드는 페이지가 핸들러를 넘긴 그리드에서만 노출 */}
							{typeof props.onExcelUploadClick === 'function' && (
								<Tooltip title={uploadTitle}>
									<span>
										<MuiButton
											variant="contained"
											sx={greenGradientBtnSx}
											className="gridRightBtn"
											onClick={onUploadClick}
										>
											<View className="gridRightBtnInner">
												<FileUploadOutlinedIcon sx={{ fontSize: 18 }} />
												<span>{uploadTitle}</span>
											</View>
										</MuiButton>
									</span>
								</Tooltip>
							)}
							<Tooltip title={printTitle}>
								<span>
									<MuiButton
										variant="contained"
										sx={blueGradientBtnSx}
										className="gridRightBtn"
										onClick={onPrintClick}
									>
										<View className="gridRightBtnInner">
											<PrintOutlinedIcon sx={{ fontSize: 18 }} />
											<span>{printTitle}</span>
										</View>
									</MuiButton>
								</span>
							</Tooltip>
							<Tooltip title={autoSizeColumnsTitle}>
								<span>
									<MuiButton
										variant="contained"
										sx={blueGradientBtnSx}
										className="gridRightBtn"
										onClick={() => apiRef.current.autosizeColumns(autosizeOptions)}
									>
										<View className="gridRightBtnInner">
											<SettingsEthernetRoundedIcon sx={{ fontSize: 18 }} />
											<span>{autoSizeColumnsTitle}</span>
										</View>
									</MuiButton>
								</span>
							</Tooltip>
						</View>
					</>
				)}
			</View>
		</GridToolbarContainer>
	)
}

const style = css({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	width: '100%',
	padding: '0.25rem 0.25rem 0.4rem',
	borderBottom: '1px solid #e2e8f0',
	gap: '0.5rem',

	'.leftToolView': {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		gap: '0.25rem',
	},

	'.rightToolGroup': {
		paddingLeft: '0.5rem',
		marginLeft: '0.5rem',
		borderLeft: '1px solid #f1f5f9',
		gap: '0.4rem',
	},

	// 우측 액션 버튼 내부 — 아이콘 + 라벨 가로 배치.
	'.gridRightBtnInner': {
		display: 'inline-flex',
		alignItems: 'center',
		gap: '0.35rem',
	},

	// 수정 모드 토글 — chip 형태로 묶어서 의미 강조 (높이는 우측 액션 버튼과 통일)
	'.actionToggle': {
		display: 'flex',
		alignItems: 'center',
		gap: '0.375rem',
		padding: '0 0.625rem 0 0.75rem',
		height: `${TOOLBAR_ITEM_HEIGHT}px`,
		marginLeft: '0.5rem',
		marginRight: '0.5rem',
		borderRadius: 999,
		backgroundColor: grey[50],
		border: `1px solid ${grey[200]}`,
		transition: 'background-color 160ms ease, border-color 160ms ease',

		'.actionToggleLabel': {
			fontSize: '0.8125rem',
			fontWeight: 500,
			color: grey[700],
			letterSpacing: '0.01em',
			transition: 'color 160ms ease',
		},
	},

	// 토글 ON — amber 강조
	'.actionToggleOn': {
		backgroundColor: 'rgba(245, 158, 11, 0.12)',
		borderColor: 'rgba(245, 158, 11, 0.45)',

		'.actionToggleLabel': {
			color: '#b45309',
			fontWeight: 600,
		},
	},

	'.leftToolbarSlot': {
		display: 'flex',
		alignItems: 'center',
		marginLeft: '0.75rem',
	},

	// 검색 인풋 — "수정 모드" 우측 chip 모양.
	// 초기엔 수정 모드 토글과 비슷한 작은 너비, 포커스 시 약 3배 확장.
	// max-width 는 clamp(=뷰포트 폭에 비례)로 제한해 우측 버튼 그룹과 부딪히지 않게.
	'.gridSearchInput': {
		display: 'inline-flex',
		alignItems: 'center',
		height: `${TOOLBAR_ITEM_HEIGHT}px`,
		padding: '0 0.625rem',
		marginLeft: '0.25rem',
		borderRadius: 999,
		border: `1px solid ${grey[200]}`,
		background: '#ffffff',
		width: '110px',
		transition: 'width 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease',
		overflow: 'hidden',
		flexShrink: 0,

		'&.focused': {
			width: 'clamp(180px, 28vw, 330px)',
			borderColor: '#94a3b8',
			boxShadow: '0 0 0 3px rgba(148, 163, 184, 0.18)',
		},

		'.searchIcon': {
			color: '#94a3b8',
			fontSize: '18px',
			flexShrink: 0,
			marginRight: '0.35rem',
		},

		input: {
			flex: 1,
			minWidth: 0,
			border: 'none',
			outline: 'none',
			background: 'transparent',
			fontSize: '0.875rem',
			color: grey[900],
			padding: 0,
			lineHeight: 1.2,
		},
	},
})
